import { useRef } from "react";
import { Film, ImageIcon, Loader2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { cn } from "@/lib/cn";
import { formatBytes, useMediaLibrary, useUploadMedia } from "@/lib/media";

export function MediaPicker({
    selected,
    onChange,
}: {
    selected: string[];
    onChange: (next: string[]) => void;
}) {
    const inputRef = useRef<HTMLInputElement>(null);
    const { data: library } = useMediaLibrary();
    const upload = useUploadMedia();

    function handleFiles(files: FileList | null) {
        const file = files?.[0];
        if (!file) return;
        upload.mutate(file, { onSuccess: (asset) => onChange([...selected, asset.id]) });
        if (inputRef.current) inputRef.current.value = "";
    }

    function toggle(id: string) {
        onChange(selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id]);
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime"
                    className="hidden"
                    onChange={(event) => handleFiles(event.target.files)}
                />
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    loading={upload.isPending}
                    onClick={() => inputRef.current?.click()}
                >
                    <Upload className="size-4" />
                    Tải ảnh / video
                </Button>
                <span className="text-xs text-ink-muted">JPG, PNG, WebP, GIF, MP4, MOV</span>
            </div>

            {upload.error && <Alert tone="danger">{upload.error.message}</Alert>}

            {library && library.length > 0 && (
                <div className="grid max-h-56 grid-cols-4 gap-2 overflow-y-auto rounded-lg border border-line p-2 md:grid-cols-6">
                    {library.map((asset) => {
                        const active = selected.includes(asset.id);
                        return (
                            <button
                                key={asset.id}
                                type="button"
                                onClick={() => toggle(asset.id)}
                                title={`${asset.filename} · ${formatBytes(asset.sizeBytes)}`}
                                className={cn(
                                    "relative aspect-square overflow-hidden rounded-lg border-2 bg-surface-muted transition",
                                    active ? "border-brand" : "border-transparent hover:border-line",
                                )}
                            >
                                {asset.kind === "IMAGE" ? (
                                    <img src={asset.url} alt="" className="size-full object-cover" />
                                ) : asset.thumbnailUrl ? (
                                    <img src={asset.thumbnailUrl} alt="" className="size-full object-cover" />
                                ) : (
                                    <Film className="absolute inset-0 m-auto size-6 text-ink-muted" />
                                )}

                                {asset.kind === "VIDEO" && (
                                    <span className="absolute bottom-1 left-1 rounded bg-ink/70 px-1 text-[10px] text-white">
                                        video
                                    </span>
                                )}
                                {active && (
                                    <span className="absolute right-1 top-1 flex size-4 items-center justify-center rounded-full bg-brand text-[10px] font-bold text-white">
                                        {selected.indexOf(asset.id) + 1}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {library?.length === 0 && !upload.isPending && (
                <p className="flex items-center gap-2 rounded-lg border border-dashed border-line px-3 py-6 text-sm text-ink-muted">
                    <ImageIcon className="size-4" />
                    Chưa có file nào. Tải ảnh hoặc video lên để đính kèm.
                </p>
            )}

            {upload.isPending && (
                <p className="flex items-center gap-2 text-sm text-ink-muted">
                    <Loader2 className="size-4 animate-spin" />
                    Đang xử lý file…
                </p>
            )}

            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selected.map((id, index) => {
                        const asset = library?.find((item) => item.id === id);
                        return (
                            <span
                                key={id}
                                className="flex items-center gap-1 rounded-full bg-brand-soft px-2 py-1 text-xs text-brand"
                            >
                                {index + 1}. {asset?.filename ?? id}
                                <button type="button" onClick={() => toggle(id)} aria-label="Bỏ chọn">
                                    <X className="size-3" />
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
